import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import WebInput from '../webInput';
import WebButton from '../webButton';
import './style.css';

const AddressForm = ({ addressData = null, onSubmit, onCancel, onClose, submitText = 'Save Address', cancelText = 'Cancel' }) => {
  const [formData, setFormData] = useState({
    label: '',
    firstName: '',
    lastName: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    addressType: 'House/Apartment',
    isDefaultShipping: false,
    isDefaultBilling: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoadingPincode, setIsLoadingPincode] = useState(false);

  const indianStates = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry',
  ];

  const addressTypes = ['House/Apartment', 'Office', 'Other'];

  // Populate form with existing address data if editing
  useEffect(() => {
    if (addressData) {
      setFormData({
        label: addressData.label || '',
        firstName: addressData.firstName || '',
        lastName: addressData.lastName || '',
        phone: addressData.phone || '',
        address1: addressData.address1 || '',
        address2: addressData.address2 || '',
        city: addressData.city || '',
        state: addressData.state || '',
        pincode: addressData.pincode || '',
        country: addressData.country || 'India',
        addressType: addressData.addressType || 'House/Apartment',
        isDefaultShipping: addressData.isDefaultShipping || false,
        isDefaultBilling: addressData.isDefaultBilling || false,
      });
    }
  }, [addressData]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Auto-fill city and state from pincode using postal API
  const handlePincodeChange = async e => {
    const pincode = e.target.value;
    handleChange(e);

    if (pincode.length === 6 && /^\d{6}$/.test(pincode)) {
      setIsLoadingPincode(true);
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        const data = await response.json();

        if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice[0]) {
          const postOffice = data[0].PostOffice[0];
          setFormData(prev => ({
            ...prev,
            city: postOffice.District || prev.city,
            state: postOffice.State || prev.state,
          }));
        }
      } catch (error) {
        console.error('Failed to fetch pincode details:', error);
      } finally {
        setIsLoadingPincode(false);
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address Line 1 is required';
    }

    if (!formData.address2.trim()) {
      newErrors.address2 = 'Address Line 2 is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <form className="address-form" onSubmit={handleSubmit}>
      {onClose && (
        <div className="address-form-header">
          <h2 className="address-form-title">{addressData ? 'Edit Address' : 'Add New Address'}</h2>
          <button type="button" className="address-form-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
      )}
      <div className="address-form-grid">
        {/* Label (Optional) */}
        <div className="address-form-field address-form-full-width">
          <WebInput
            type="text"
            name="label"
            label="Address Label (Optional)"
            placeholder="e.g., Home, Office, Mom's place"
            value={formData.label}
            onChange={handleChange}
            maxLength={50}
            parentClassName="web_input_parent"
          />
        </div>

        {/* First Name */}
        <div className="address-form-field">
          <WebInput
            type="text"
            name="firstName"
            label="First Name *"
            placeholder="Enter first name"
            value={formData.firstName}
            onChange={handleChange}
            error={errors.firstName}
            parentClassName="web_input_parent"
          />
        </div>

        {/* Last Name */}
        <div className="address-form-field">
          <WebInput
            type="text"
            name="lastName"
            label="Last Name *"
            placeholder="Enter last name"
            value={formData.lastName}
            onChange={handleChange}
            error={errors.lastName}
            parentClassName="web_input_parent"
          />
        </div>

        {/* Phone */}
        <div className="address-form-field address-form-full-width">
          <WebInput
            type="tel"
            name="phone"
            label="Phone Number *"
            placeholder="10-digit mobile number"
            value={formData.phone}
            onChange={handleChange}
            error={errors.phone}
            maxLength={10}
            parentClassName="web_input_parent"
          />
        </div>

        {/* Address Line 1 */}
        <div className="address-form-field address-form-full-width">
          <WebInput
            type="text"
            name="address1"
            label="Address Line 1 *"
            placeholder="House No., Building Name"
            value={formData.address1}
            onChange={handleChange}
            error={errors.address1}
            parentClassName="web_input_parent"
          />
        </div>

        {/* Address Line 2 */}
        <div className="address-form-field address-form-full-width">
          <WebInput
            type="text"
            name="address2"
            label="Address Line 2 *"
            placeholder="Road name, Area, Colony"
            value={formData.address2}
            onChange={handleChange}
            error={errors.address2}
            parentClassName="web_input_parent"
          />
        </div>

        {/* Pincode (with auto-fill) */}
        <div className="address-form-field">
          <WebInput
            type="text"
            name="pincode"
            label="Pincode *"
            placeholder="6-digit pincode"
            value={formData.pincode}
            onChange={handlePincodeChange}
            error={errors.pincode}
            maxLength={6}
            parentClassName="web_input_parent"
          />
          {isLoadingPincode && <p className="address-form-info-text">Fetching location...</p>}
        </div>

        {/* City */}
        <div className="address-form-field">
          <WebInput
            type="text"
            name="city"
            label="City *"
            placeholder="Enter city"
            value={formData.city}
            onChange={handleChange}
            error={errors.city}
            parentClassName="web_input_parent"
          />
        </div>

        {/* State */}
        <div className="address-form-field address-form-full-width">
          <label className="address-form-label">State *</label>
          <select name="state" value={formData.state} onChange={handleChange} className={`address-form-select ${errors.state ? 'address-form-select-error' : ''}`}>
            <option value="">Select State</option>
            {indianStates.map(state => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          {errors.state && <p className="address-form-error">{errors.state}</p>}
        </div>

        {/* Address Type */}
        <div className="address-form-field address-form-full-width">
          <label className="address-form-label">Address Type</label>
          <select name="addressType" value={formData.addressType} onChange={handleChange} className="address-form-select">
            {addressTypes.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Default Checkboxes */}
        <div className="address-form-field address-form-full-width">
          <div className="address-form-checkboxes">
            <label className="address-form-checkbox-label">
              <input type="checkbox" name="isDefaultShipping" checked={formData.isDefaultShipping} onChange={handleChange} className="address-form-checkbox" />
              <span>Set as default shipping address</span>
            </label>

            <label className="address-form-checkbox-label">
              <input type="checkbox" name="isDefaultBilling" checked={formData.isDefaultBilling} onChange={handleChange} className="address-form-checkbox" />
              <span>Set as default billing address</span>
            </label>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="address-form-actions">
        <WebButton type="button" onClick={onCancel} className="address-form-cancel-btn">
          {cancelText}
        </WebButton>
        <WebButton type="submit" className="address-form-submit-btn">
          {submitText}
        </WebButton>
      </div>
    </form>
  );
};

export default AddressForm;
